import React, {useEffect} from 'react';
import TextareaAutosize from 'react-autosize-textarea';

import classnames from 'classnames';

import {ErrorMessage} from '../form/error-message';
import {Pencil} from '../icon';

import styles from './editable-label-wrapping.module.css';

type Props = {
    value: string;
    onSaveNewValue: (arg1: string) => Promise<any>;
    placeholder: string;
    customStyle?: any;
    allowSavingEmpty?: boolean;
    required?: boolean;
    shouldFocusOnMount: boolean;
    shouldSaveOnEnter?: boolean;
    showPlaceholderWhenEmpty?: boolean;
    onFocusedOnMount?: () => void;
    errorMessage?: string | null | undefined;
    isEditableIconVisible?: boolean;
    disableOnSaving?: boolean;
};

export function EditableLabelWrapping(props: Props) {
    const {value: propsValue} = props;
    const isShowingPlaceholder = !props.value && Boolean(props.showPlaceholderWhenEmpty);
    const displayValue = props.value || (props.showPlaceholderWhenEmpty ? props.placeholder : '');

    const {shouldFocusOnMount, onFocusedOnMount} = props;
    const [isEditing, setIsEditing] = React.useState<boolean>(false);
    const [isHovered, setIsHovered] = React.useState<boolean>(false);
    const [value, setValue] = React.useState<string>(propsValue);
    const [isSaving, setIsSaving] = React.useState<boolean>(false);

    const buttonClassName = classnames(
        props.required ? styles['button--required'] : styles.button,
        {
            [styles['button--empty']]: isShowingPlaceholder,
            [styles['button--saving']]: props.disableOnSaving && isSaving,
        }
    );

    const inputRef = React.useRef();

    // This seems unnecessary, but if the props.value changes, we need to manually
    // set the value in state to get that change or the TextareaAutosize won't get the
    // new value from props.
    React.useEffect(() => {
        setValue(propsValue);
    }, [propsValue]);

    useEffect(() => {
        if ((isEditing || shouldFocusOnMount) && inputRef && inputRef.current) {
            // @ts-expect-error
            inputRef.current.select();

            // If we focused on mount, set editing to true, but tell
            // parent component that we don't want to re-focus after blurring
            if (shouldFocusOnMount) {
                setIsEditing(true);
                // callback to parent to set that state to false
                onFocusedOnMount?.();
            }
        }
    }, [inputRef, isEditing, shouldFocusOnMount, onFocusedOnMount]);

    const handleSave = () => {
        if (props.disableOnSaving && isSaving) {
            return;
        }

        if ((!props.allowSavingEmpty && !value.length) || props.value === value) {
            setValue(propsValue);
            setIsEditing(false);
            return;
        }

        if (props.disableOnSaving) {
            setIsSaving(true);
        }

        props
            .onSaveNewValue(value)
            .then(() => {
                if (props.disableOnSaving) {
                    setIsSaving(false);
                }
            })
            .catch(() => {
                if (props.disableOnSaving) {
                    setIsSaving(false);
                }
            });
        setIsEditing(false);
    };

    return (
        <div className={styles.container}>
            <div className='flex flex-dir-col full-width'>
                {isEditing || shouldFocusOnMount ? (
                    <TextareaAutosize
                        style={{...props.customStyle, resize: 'none'}}
                        value={value}
                        autoFocus={true}
                        placeholder={props.placeholder}
                        onChange={(e) => {
                            // @ts-expect-error
                            setValue(e.target.value);
                        }}
                        onKeyDown={(e) => {
                            if (props.shouldSaveOnEnter && e.key === 'Enter') {
                                e.preventDefault();
                                handleSave();
                            }
                        }}
                        onBlur={handleSave}
                        ref={(node) => {
                            // @ts-expect-error
                            inputRef.current = node;
                        }}
                        className={styles.input}
                    />
                ) : (
                    <button
                        disabled={props.disableOnSaving && isSaving}
                        onClick={() => {
                            setValue(propsValue);
                            setIsHovered(false);
                            setIsEditing(true);
                        }}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                        style={props.customStyle}
                        className={buttonClassName}
                    >
                        {displayValue}
                        {(isHovered || props.isEditableIconVisible) && (
                            <Pencil data-focus-element='label' size={12} />
                        )}
                    </button>
                )}
                {props.errorMessage ? <ErrorMessage>{props.errorMessage}</ErrorMessage> : undefined}
            </div>
        </div>
    );
}
